import { MigratableJson } from '../MigratableJson';
import {
  FORM_DROPDOWN_FIELD_NODE_NAME,
  FORM_SELECTION_FIELD_NODE_NAME,
} from '@builder4/shared/utils/constants';

/**
 * Migration v14: Migrate FormDropdownField and FormSelectionField from string-based defaults to index-based defaults
 *
 * This migration converts the deprecated defaultOption/defaultOptions (string-based)
 * properties to defaultIndices (index-based) for FormDropdownField and FormSelectionField components.
 * Both single-select and multi-select use defaultIndices for consistency.
 *
 * Why index-based defaults?
 * - Handles duplicate option names correctly (two options with the same text)
 * - More reliable when options are reordered
 *
 * FormDropdownField migration:
 * - For single-select: Convert defaultOption (string) or defaultIndex (number) to defaultIndices
 *   Single-select uses defaultIndices[0] as the selected option
 * - For multi-select: Convert defaultOptions (string[]) to defaultIndices (number[])
 *   by finding the indices of each option in the options array
 * - Remove the deprecated defaultOption, defaultOptions, and defaultIndex properties
 *
 * FormSelectionField migration:
 * - Convert defaultOption (string) to defaultIndices by finding index in options array
 * - Remove the deprecated defaultOption property
 */
export default function toV14<T extends MigratableJson>(data: T): T {
  const allNodes = data.getAllNodes();

  for (const { node } of allNodes) {
    // Migrate FormDropdownField
    if (node.displayName === FORM_DROPDOWN_FIELD_NODE_NAME) {
      const props = { ...(node.props || {}) } as Record<string, unknown>;
      const options = (props.options as string[]) || [];
      const isSingleSelect = props.isSingleSelect !== false; // Default is true (single-select)

      if (isSingleSelect) {
        // Single-select migration: defaultOption/defaultIndex -> defaultIndices
        if (props.defaultOption && typeof props.defaultOption === 'string') {
          const defaultOption = props.defaultOption as string;
          const index = options.indexOf(defaultOption);
          props.defaultIndices = index >= 0 ? [index] : [];
        } else if (typeof props.defaultIndex === 'number') {
          // Migrate from old defaultIndex format
          props.defaultIndices = [props.defaultIndex];
        }
        // Ensure defaultIndices exists
        if (!Array.isArray(props.defaultIndices)) {
          props.defaultIndices = [];
        }
      } else {
        // Multi-select migration: defaultOptions -> defaultIndices
        if (Array.isArray(props.defaultOptions)) {
          const defaultOptions = props.defaultOptions as string[];
          const indices: number[] = [];
          for (const opt of defaultOptions) {
            const index = options.indexOf(opt);
            if (index >= 0) {
              indices.push(index);
            }
          }
          props.defaultIndices = indices;
        }
        // Ensure defaultIndices exists
        if (!Array.isArray(props.defaultIndices)) {
          props.defaultIndices = [];
        }
      }

      // Remove deprecated properties
      delete props.defaultOption;
      delete props.defaultOptions;
      delete props.defaultIndex;

      node.props = props;
    }

    // Migrate FormSelectionField
    if (node.displayName === FORM_SELECTION_FIELD_NODE_NAME) {
      const props = { ...(node.props || {}) } as Record<string, unknown>;
      const options = (props.options as string[]) || [];

      // SelectionField: migrate defaultOption -> defaultIndices
      if (props.defaultOption && typeof props.defaultOption === 'string') {
        const defaultOption = props.defaultOption as string;
        const index = options.indexOf(defaultOption);
        props.defaultIndices = index >= 0 ? [index] : [];
      }

      // Ensure defaultIndices exists
      if (!Array.isArray(props.defaultIndices)) {
        props.defaultIndices = [];
      }

      // Remove deprecated property
      delete props.defaultOption;

      node.props = props;
    }
  }

  return data;
}
